import { Avatar, Divider, Modal, Skeleton, Stack, Tooltip, Typography } from '@common/components'
import { handleGravatar } from '@common/utils/gravatar'
import { IAgoraRTCRemoteUser } from '@features/agora/lib/agora-rtc'
import ReportUser from '@features/auth/components/report-user/report-user'
import FollowUser from '@features/auth/components/follow-user'
import { useCurrentEvent } from '@features/event/hooks'
import { FavoriteBorderIcon } from '@common/icons'
import {
  GoalsQuestionnaireDataResponse,
  useAuthGetFollowersQuery,
  useAuthGetUserGoalsQuery,
} from '@app/services/auto-generated/auth'
import AvoidUser from '@features/auth/components/avoid-user'
import goalIcon from '@assets/svg/goal-icon.svg'
import workIcon from '@assets/svg/work-icon.svg'
import puzzleIcon from '@assets/svg/puzzle.svg'
import { useTheme } from '@features/theme/theme.hooks'

const UserDetailsModal = (props: { open: boolean; userId: string; handleClose: () => void }) => {
  const { open, userId, handleClose } = props
  const { isDark } = useTheme()
  const { event } = useCurrentEvent()
  const userDetails = event?.users?.find((u) => String(u.id) === userId)
  const { data: listFollowers, isLoading } = useAuthGetFollowersQuery(
    {
      userId: userDetails?.id as number,
    },
    { refetchOnMountOrArgChange: true, skip: !open },
  )
  const { data: userGoal, isLoading: isLoadingGoals } = useAuthGetUserGoalsQuery(
    {
      userId: userDetails?.id as number,
    },
    { refetchOnMountOrArgChange: true, skip: !open },
  )
  if (!open || !userDetails) return null
  return (
    <Modal
      open={open}
      onClose={handleClose}
      disableEnforceFocus
      disableAutoFocus
      paperProps={{
        sx: {
          borderRadius: '10px',
          p: 3,
          width: '450px',
          minHeight: '85vh',
          maxWidth: '90%',
          textAlign: 'center',
          maxHeight: '95vh',
          overflowY: 'auto',
        },
      }}
    >
      <Stack spacing={2}>
        <Stack alignItems={'center'} spacing={2}>
          <Avatar
            src={handleGravatar(userDetails?.image ?? '')}
            sx={{ width: '80px', height: '80px' }}
          />
          <Typography fontWeight={600}>
            {userDetails?.display_name || userDetails?.username || ''}
          </Typography>
          <Tooltip title='Followers'>
            {isLoadingGoals || isLoading ? (
              <Skeleton variant='rectangular' height={30} width={150} />
            ) : (
              <Stack direction={'row'} alignItems={'center'} spacing={0.5}>
                <FavoriteBorderIcon fontSize='small' />
                <Typography>{listFollowers?.length}</Typography>
              </Stack>
            )}
          </Tooltip>
        </Stack>
        <Stack direction={'row'} alignItems={'stretch'} justifyContent={'center'} spacing={0.5}>
          <ReportUser icon userToReport={userDetails} />
          <AvoidUser userAvoid={userDetails} />
          <FollowUser userToFollow={userDetails} />
        </Stack>
        <GoalsForm isLoading={isLoadingGoals || isLoading} userGoal={userGoal} />
      </Stack>
    </Modal>
  )
}

export const GoalsForm = (props: {
  isLoading: boolean
  userGoal: GoalsQuestionnaireDataResponse | undefined
}) => {
  const { isDark } = useTheme()
  const { isLoading, userGoal } = props
  return (
    <>
      {isLoading ? (
        <Stack alignItems={'center'} spacing={1}>
          <Skeleton variant='rectangular' height={40} sx={{ width: '100%' }} />
          <Skeleton variant='rectangular' height={40} sx={{ width: '100%' }} />
          <Skeleton variant='rectangular' height={40} sx={{ width: '100%' }} />
        </Stack>
      ) : (
        <>
          {userGoal?.about_me && (
            <>
              <Typography textAlign={'start'} sx={{ px: 1, width: '100%' }}>
                {userGoal?.about_me}
              </Typography>
            </>
          )}
          <Divider />
          <Stack direction={'row'} alignItems={'center'} spacing={1}>
            {userGoal?.occupation?.status !== '' && userGoal?.occupation?.description !== '' && (
              <img
                src={workIcon}
                alt='work'
                width={20}
                style={{ filter: isDark ? 'brightness(0) invert(1)' : 'initial' }}
              />
            )}
            {userGoal && (
              <>
                {userGoal?.occupation?.description !== '' && (
                  <Typography sx={{ px: 1 }}>{userGoal?.occupation?.description}</Typography>
                )}
              </>
            )}
          </Stack>
          {userGoal && userGoal?.goal?.length > 0 && (
            <Stack direction={'row'} alignItems={'flex-start'} spacing={1}>
              <img
                src={goalIcon}
                alt='goal'
                width={20}
                style={{
                  paddingTop: '4px',
                  filter: isDark ? 'brightness(0) invert(1)' : 'initial',
                }}
              />
              <Stack direction={'row'} alignItems={'center'} gap={1} flexWrap={'wrap'}>
                {userGoal?.goal?.map((goal, index) => (
                  <Typography key={index} sx={{ border: '1px solid', px: 1, borderRadius: '5px' }}>
                    {goal}
                  </Typography>
                ))}
              </Stack>
            </Stack>
          )}
          {userGoal && userGoal?.interests?.length > 0 && (
            <Stack direction={'row'} alignItems={'flex-start'} spacing={1}>
              <img
                src={puzzleIcon}
                alt='interests'
                width={20}
                style={{
                  paddingTop: '4px',
                  filter: isDark ? 'brightness(0) invert(1)' : 'initial',
                }}
              />
              <Stack direction={'row'} alignItems={'center'} gap={1} flexWrap={'wrap'}>
                {userGoal?.interests?.map((interest, index) => (
                  <Typography
                    key={index}
                    sx={{
                      border: (theme) => `1px solid ${theme.palette.primary.main}`,
                      color: (theme) => `${theme.palette.primary.main}`,
                      px: 1,
                      borderRadius: '5px',
                    }}
                  >
                    {interest}
                  </Typography>
                ))}
              </Stack>
            </Stack>
          )}
        </>
      )}
    </>
  )
}
export default UserDetailsModal
